import { useState, useEffect, useRef } from 'react'
import { Drawer, Button, Input, Descriptions, Tag } from 'antd'
import { CloseOutlined, CheckCircleOutlined, FlagOutlined, LockOutlined, SaveOutlined } from '@ant-design/icons'
import { AssetReviewStatus, type ComplianceAssetReviewItem } from '@/features/compliance/types'
import { useUpdateAssetReviewMutation } from '@/features/compliance/hooks'
import { formatComplianceAmount, type ComplianceRowStatus } from '@/features/compliance/utils'
import { useToast } from '@/contexts/ToastContext'

const { TextArea } = Input

type AccountDetailDrawerProps = {
  clientId: string
  jobId: string
  open: boolean
  onClose: () => void
  item: (ComplianceAssetReviewItem & { rowStatus: ComplianceRowStatus }) | null
  reviewState?: { status?: AssetReviewStatus; notes?: string }
  onReviewUpdated?: (jobStatus?: string | null) => void | Promise<void>
}


export function AccountDetailDrawer({
  clientId,
  jobId,
  open,
  onClose,
  item,
  reviewState,
  onReviewUpdated,
}: AccountDetailDrawerProps) {
  const [notes, setNotes] = useState(reviewState?.notes || '')
  const lastItemIdRef = useRef<string | null>(null)
  const { addToast } = useToast()
  const updateReviewMutation = useUpdateAssetReviewMutation(clientId, jobId)

  useEffect(() => {
    if (!item) {
      lastItemIdRef.current = null
      return
    }
    if (lastItemIdRef.current !== item.complianceAssetId) {
      lastItemIdRef.current = item.complianceAssetId
      setNotes(reviewState?.notes ?? item.reviewNotes ?? '')
    }
  }, [item, item?.complianceAssetId, reviewState?.notes, item?.reviewNotes])

  if (!item) return null

  const hasWorkpaper = item.matchedWorkpaperFileName?.trim() && item.matchedWorkpaperFileName !== 'No workpaper found'
  const tbAmount = formatComplianceAmount(item.amount)
  const wpAmount = formatComplianceAmount(item.workpaperAmount)
  const legacySheets = Array.isArray(item.matchedWorkpaperSheets) ? item.matchedWorkpaperSheets : []
  const sheetsText = legacySheets.length
    ? legacySheets.join(', ')
    : (item.sheetName?.trim() || '')

  const handleSaveNotes = () => {
    void handleReviewUpdate({ notes })
  }

  const handleReviewUpdate = async (data: { status?: AssetReviewStatus; notes?: string }) => {
    if (!item) return
    try {
      const result = await updateReviewMutation.mutateAsync({
        complianceAssetId: item.complianceAssetId,
        data,
      })
      if (data.status) addToast('Review status updated', 'success')
      if (data.notes !== undefined) addToast('Notes saved', 'success')
      await Promise.resolve(onReviewUpdated?.(result.jobStatus))
    } catch (e) {
      addToast(
        e instanceof Error
          ? e.message
          : data.status
            ? 'Failed to update review status'
            : 'Failed to update notes',
        'error'
      )
    }
  }

  const getStatusColor = () => {
    if (item.rowStatus === 'match') return 'green'
    if (item.rowStatus === 'mismatch') return 'red'
    return 'orange'
  }

  const getStatusText = () => {
    if (item.rowStatus === 'match') return 'Match'
    if (item.rowStatus === 'mismatch') return 'Mismatch'
    return 'Missing'
  }

  return (
    <Drawer
      title={
        <div className="flex items-center justify-between">
          <span className="font-semibold">{item.assetName}</span>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={480}
      className="account-detail-drawer"
      closable={false}
    >
      <div className="flex flex-col gap-6">
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">TRIAL BALANCE</h3>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="TB Amount">{tbAmount}</Descriptions.Item>
            <Descriptions.Item label="Type">{item.class?.trim() || '—'}</Descriptions.Item>
          </Descriptions>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">MATCHED WORKPAPER</h3>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="File">
              {hasWorkpaper ? (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                  }}
                  className="feature-link"
                >
                  <span className="whitespace-normal break-words">{item.matchedWorkpaperFileName}</span>
                </a>
              ) : (
                'No workpaper found'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Sheets">
              {sheetsText ? <span className="whitespace-normal break-words">{sheetsText}</span> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="WP Amount">{wpAmount}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor()}>{getStatusText()}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">REVIEWER NOTES</h3>
          <TextArea
            placeholder="Add notes for this line item..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full mb-2"
          />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveNotes}
            loading={updateReviewMutation.isPending}
            disabled={updateReviewMutation.isPending}
          >
            Save
          </Button>
        </div>

        <div className="flex flex-col gap-2 pt-4 border-t border-slate-200">
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => void handleReviewUpdate({ status: AssetReviewStatus.Reviewed })}
            className="w-full"
            loading={updateReviewMutation.isPending}
            disabled={updateReviewMutation.isPending}
          >
            Mark as Reviewed
          </Button>
          <Button
            icon={<FlagOutlined />}
            onClick={() => void handleReviewUpdate({ status: AssetReviewStatus.NeedsReview })}
            className="w-full"
            loading={updateReviewMutation.isPending}
            disabled={updateReviewMutation.isPending}
          >
            Flag for Follow-up
          </Button>
          <Button
            icon={<LockOutlined />}
            onClick={() => void handleReviewUpdate({ status: AssetReviewStatus.Locked })}
            className="w-full"
            loading={updateReviewMutation.isPending}
            disabled={updateReviewMutation.isPending}
          >
            Lock Item
          </Button>
        </div>
      </div>
    </Drawer>
  )
}
